import { ApolloCache, useLazyQuery, useMutation } from '@apollo/client';
import { ADD_USER_ROLES, CREATE_USER_MEMO, CREATE_USER_PROFILE, GET_USER_ASSIGNMENT, GET_USER_MEMOS, GET_USER_PROFILE, IS_USER_EXISTS, REMOVE_USER_ROLES, SEARCH_SECURITY_USERS, UPDATE_USER } from '../../../graphql/user-gql';
import { FetchPolicyOption } from '../../../services/apollo/apollo';
import { Action } from '../../../types/reducer-action';
import { IAssignedRole, IAssignedRoleInput, IMemo, ISearchUsersInput, ISearchUsersResult, IUserAssignment, IUserInfoInput, IUserProfile } from '../../../types/user';
import { equals, isPositiveNumber, toOrdered, trimToEmpty } from '../../../utils/commons';
import { fullNameAutoFormat } from '../../../utils/format-fullname';
import { ApolloErrorAction, DataEntryEvent } from '../../common/data-entry-reducer';
import { UserManagerDomain, UserProfileForm, useUserDispatch } from './user-manager-context';

export const toUserProfileForm = (userProfile?: IUserProfile) => userProfile ?
  {
    ...userProfile,
    individualName: fullNameAutoFormat(userProfile.firstName, userProfile.middleName, userProfile.lastName)
  } as UserProfileForm
  : undefined;

const toUserInfoInput = (form?: UserProfileForm) => form ?
  {
    individualId: form.individualId,
    email: form.email,
    assignmentId: form.assignment?.id,
    expired: form.expired,
  } as IUserInfoInput
  : undefined;

const toUserRolesInput = (roles: IAssignedRole[]) => roles.map(role => (
  {
    userRoleTypeId: role.userRoleTypeId,
    orgId: role.orgScope?.id,
  } as IAssignedRoleInput
));

interface UserApi {
  searchUsers: (input: ISearchUsersInput) => Promise<ISearchUsersResult[] | undefined>;
  getUserProfile: (userAccountId: number) => Promise<IUserProfile | undefined>;
  getUserAssignment: (assignmentId: number) => Promise<IUserAssignment | undefined>;
  createUserProfile: (form: UserProfileForm) => Promise<boolean>;
  updateUser: (userAccountId: number, form: UserProfileForm) => Promise<IUserProfile | undefined>;
  addUserRoles: (userAccountId: number, roles: IAssignedRole[]) => Promise<boolean>;
  removeUserRoles: (userAccountId: number, roles: IAssignedRole[]) => Promise<boolean>;
  checkUserEmail: (email: string) => Promise<boolean>; // true: email is not used
  getUserMemos: (userAccountId: number) => Promise<IMemo[] | undefined>;
  createUserMemo: (userAccountId: number, content: string) => Promise<boolean>;
  busy: boolean;
}

interface GeteUserMemosResponse { userMemos: IMemo[] }
interface CreateUserMemoResponse { createUserMemo: IMemo }

export default function useUserApi(): UserApi {
  const dispatch = useUserDispatch();

  const [search, { loading: searching }] = useLazyQuery<{ securityUsers: ISearchUsersResult[] }>(SEARCH_SECURITY_USERS, FetchPolicyOption.NetworkOnly);

  const [getProfile, { loading }] = useLazyQuery<{ userProfile: IUserProfile }>(GET_USER_PROFILE, FetchPolicyOption.NetworkOnly);

  const [getAssignment, { loading: loadingAssignment }] = useLazyQuery<{ userAssignment: IUserAssignment }>(GET_USER_ASSIGNMENT);

  const [createProfile, { loading: creating }] = useMutation<{ createUserProfile: number }>(CREATE_USER_PROFILE);

  const [updateUserInfo, { loading: updating }] = useMutation<{ updateUser: IUserProfile }>(UPDATE_USER);

  const [addRoles, { loading: addingRoles }] = useMutation<{ addUserRoles: IAssignedRole[] }>(ADD_USER_ROLES);

  const [removeRoles, { loading: removingRoles }] = useMutation<{ removeUserRoles: IAssignedRole[] }>(REMOVE_USER_ROLES);

  const [isUserExists, { loading: checkingEmail }] = useLazyQuery<{ isUserExists: boolean }>(IS_USER_EXISTS, FetchPolicyOption.NoCache);

  const [getMemos, { loading: fetchingMemos }] = useLazyQuery<GeteUserMemosResponse>(GET_USER_MEMOS);

  const [createMemo, { loading: creatingMemo }] = useMutation<CreateUserMemoResponse>(CREATE_USER_MEMO);

  const searchUsers = (input: ISearchUsersInput) => {
    dispatch(new Action(DataEntryEvent.NewDataSet));

    return search({ variables: { input } })
      .then(response => {
        let results = response.data?.securityUsers;

        if (isPositiveNumber(input.orgId)) {
          results = toOrdered(results,
            (item) => item.assignedRoles?.some(ar => equals(ar.orgScope?.id, input.orgId)) ? -1 : 0
          );
        }

        dispatch(new Action(DataEntryEvent.NewDataSet, results));

        return results;
      });
  };

  const getUserProfile = (userAccountId: number) => {
    dispatch(new Action(DataEntryEvent.SetDataDetails));

    return getProfile({
      variables: {
        userAccountId: userAccountId
      }
    }).then(response => {
      const userProfile = response?.data?.userProfile;
      userProfile && dispatch(new Action(DataEntryEvent.SetDataDetails, toUserProfileForm(userProfile)));
      return userProfile;
    });
  };

  const getUserAssignment = (assignmentId: number) =>
    getAssignment({
      variables: {
        assignmentId: assignmentId
      }
    }).then(response => response?.data?.userAssignment);

  const createUserProfile = async (form: UserProfileForm) => {
    const user = toUserInfoInput(form);
    const roles = toUserRolesInput(form.assignedRoles);
    return !!user && createProfile({
      variables: {
        user: user,
        roles: roles
      }
    }).then((response) => !!response.data?.createUserProfile);
  };

  const updateUser = async (userAccountId: number, form: UserProfileForm) => {
    const input = toUserInfoInput(form);
    return input && updateUserInfo({
      variables: {
        userAccountId: userAccountId,
        user: input
      },
      onError: (error) => dispatch(new ApolloErrorAction(error))
    }).then((response) => {
      const updatedUser = response.data?.updateUser;
      dispatch(new Action(DataEntryEvent.UpdateData, updatedUser));
      return updatedUser;
    });
  };

  const addUserRoles = async (userAccountId: number, roles: IAssignedRole[]) => {
    const input = toUserRolesInput(roles);
    return addRoles({
      variables: {
        userAccountId: userAccountId,
        roles: input
      },
      update: (cache, { data }) => _refreshAssignedRoles(cache, userAccountId, data?.addUserRoles)
    }).then(response => !!response.data?.addUserRoles);
  };

  const removeUserRoles = async (userAccountId: number, roles: IAssignedRole[]) => {
    const input = toUserRolesInput(roles);
    return removeRoles({
      variables: {
        userAccountId: userAccountId,
        roles: input
      },
      update: (cache, { data }) => _refreshAssignedRoles(cache, userAccountId, data?.removeUserRoles)
    }).then(response => !!response.data?.removeUserRoles);
  };

  const checkUserEmail = async (email: string) => {
    if (!trimToEmpty(email)) return true;

    return isUserExists({
      variables: {
        email: email
      }
    }).then(response => {
      const found = !!response.data?.isUserExists;
      found && dispatch(new Action(DataEntryEvent.AddErrors,
        { email: 'A user account with the same email address already exists.' }
      ));
      return !found;
    });
  };

  const getUserMemos = (userAccountId: number) => {
    dispatch(new Action(UserManagerDomain.Memos + DataEntryEvent.NewDataSet));

    return getMemos({
      variables: {
        userAccountId: userAccountId
      }
    }).then(response => {
      const memos = response?.data?.userMemos;
      dispatch(new Action(UserManagerDomain.Memos + DataEntryEvent.NewDataSet, memos));
      return memos;
    });
  };

  const createUserMemo = async (userAccountId: number, content: string) => {
    const text = trimToEmpty(content);
    return text && createMemo({
      variables: {
        userAccountId: userAccountId,
        content: text
      },
      update: (cache, { data }) => {
        const createResult = data?.createUserMemo;
        if (!createResult) return;

        const results = cache.readQuery<GeteUserMemosResponse>({
          query: GET_USER_MEMOS,
          variables: { userAccountId: userAccountId }
        })?.userMemos ?? [];

        const newResults = [createResult, ...results];

        cache.writeQuery({
          query: GET_USER_MEMOS,
          data: { userMemos: newResults },
          variables: { userAccountId: userAccountId }
        });

        dispatch(new Action(UserManagerDomain.Memos + DataEntryEvent.NewDataSet, newResults));
      }
    }).then(response => !!response.data?.createUserMemo);
  };

  function _refreshAssignedRoles(cache: ApolloCache<any>, userAccountId: number, roles?: IAssignedRole[]) {
    if (!roles) return;

    dispatch(new Action(DataEntryEvent.UpdateForm, { assignedRoles: roles }));

    dispatch(new Action(DataEntryEvent.UpdateData, {
      id: userAccountId,
      assignedRoles: roles
    }));

    cache.modify({
      id: 'UserProfile:' + userAccountId,
      fields: { assignedRoles: () => roles },
    });
  }

  const busy = searching || checkingEmail
    || loading || loadingAssignment
    || creating || updating
    || addingRoles || removingRoles
    || fetchingMemos || creatingMemo;

  return {
    searchUsers,
    getUserProfile,
    getUserAssignment,
    createUserProfile,
    updateUser,
    addUserRoles,
    removeUserRoles,
    checkUserEmail,
    getUserMemos,
    createUserMemo,
    busy
  };
}
